/**
 * Converts Unity sketch overlay JSON to Timeline annotation data.
 *
 * Unity sketch overlays use center-origin coordinates with both axes normalized
 * by content width. Timeline annotations use 0–1 normalized, top-left origin.
 *
 * Pure utility — no store or React dependencies.
 */

import type { AnnotationData } from '@/components/AnnotationCanvas.tsx';

// ─── Unity JSON structure (matches C# SketchOverlay*StorageDefinition) ────

interface SketchOverlayGroupData {
  originalAspectRatio: number; // width / height, raw float (e.g. 1.778 for 16:9)
  sketchOverlaySegmentStoragDefinitionArray: SketchOverlaySegment[]; // note: "Storag" typo matches Unity
}

interface SketchOverlaySegment {
  drawToolType: number; // 0 = Undefined, 1 = Pen, 2 = Marker
  lineColorRed: number;   // 0–1000
  lineColorGreen: number; // 0–1000
  lineColorBlue: number;  // 0–1000
  sketchOverlaySegmentPointStorageDefinitionArray: SketchOverlayPoint[];
}

interface SketchOverlayPoint {
  x: number; // int, stored as RoundToInt(1000 * position.x)
  y: number; // int, stored as RoundToInt(1000 * position.y)
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Convert a 0–1000 int RGB triple to a CSS hex color string. */
export function sketchColorToHex(r: number, g: number, b: number): string {
  const toByte = (v: number) => Math.max(0, Math.min(255, Math.round((v / 1000) * 255)));
  const rr = toByte(r).toString(16).padStart(2, '0');
  const gg = toByte(g).toString(16).padStart(2, '0');
  const bb = toByte(b).toString(16).padStart(2, '0');
  return `#${rr}${gg}${bb}`;
}

// ─── Main converter ───────────────────────────────────────────────────────

/**
 * Convert a Unity sketch overlay JSON string to Timeline AnnotationData.
 * Returns null if the JSON is invalid or contains no valid strokes.
 */
export function sketchOverlayToAnnotation(sketchJson: string): AnnotationData | null {
  let data: SketchOverlayGroupData;
  try {
    data = JSON.parse(sketchJson);
  } catch {
    return null;
  }

  const segments = data?.sketchOverlaySegmentStoragDefinitionArray;
  if (!Array.isArray(segments) || segments.length === 0) return null;

  // originalAspectRatio is a raw float (width / height), e.g. 1.778 for 16:9
  const ar = data.originalAspectRatio || 1.0;

  const paths: Array<{ d: string; stroke: string; strokeWidth: number }> = [];

  for (const seg of segments) {
    const points = seg?.sketchOverlaySegmentPointStorageDefinitionArray;
    if (!Array.isArray(points) || points.length < 2) continue;

    // Convert points to SVG M/L path commands
    // Unity stores ints = RoundToInt(1000 * normalizedPosition)
    // normalizedPosition is center-origin, range ±500, both axes scaled by content width
    // Timeline: 0–1 normalized, top-left origin
    const parts: string[] = [];
    for (let i = 0; i < points.length; i++) {
      // Undo int storage (÷1000), then convert center-origin ±500 to 0–1
      const rawX = points[i].x / 1000; // back to ±500 range
      const rawY = points[i].y / 1000;
      const tx = (rawX + 500) / 1000;          // center-origin → 0–1
      const ty = 0.5 - (rawY * ar) / 1000;     // flip Y, scale by aspect ratio
      const cmd = i === 0 ? 'M' : 'L';
      parts.push(`${cmd}${tx.toFixed(4)},${ty.toFixed(4)}`);
    }

    paths.push({
      d: parts.join(' '),
      stroke: sketchColorToHex(seg.lineColorRed ?? 0, seg.lineColorGreen ?? 0, seg.lineColorBlue ?? 0),
      strokeWidth: seg.drawToolType === 2 ? 6 : 3, // Marker = 6, Pen/default = 3
    });
  }

  if (paths.length === 0) return null;

  return { paths, viewBox: '0 0 1 1' };
}
